import React from 'react';
import {
  Box, Paper, Typography, Stack, Chip, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Tooltip
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';

interface Props {
  csvFileName?: string | null;
  csvRowCount?: number | null;
  bypassedAt?: string | Date | null;
  results: any[];
}

/**
 * Renders the source CSV as a side panel for bypass-created reports.
 * Drop-in replacement for the PDFViewer on the left side of the Review page
 * when `report.bypassInfo?.source === 'csv'`.
 *
 * Reads from `extractedData.results[]` (the parsed CSV) — same data
 * the right-hand panel uses, presented here in a flat table so the
 * reviewer can quickly cross-check that the upload was parsed correctly.
 */
const BypassCsvPanel: React.FC<Props> = ({ csvFileName, csvRowCount, bypassedAt, results }) => {
  return (
    <Paper variant="outlined" sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      {/* Header banner */}
      <Box sx={{
        p: 1.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'warning.lighter',
      }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <DescriptionIcon color="warning" fontSize="small" />
          <Box flexGrow={1}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Source CSV
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {csvFileName || 'bypass.csv'}
              {csvRowCount ? ` · ${csvRowCount} rows` : null}
              {bypassedAt ? ` · ${new Date(bypassedAt).toLocaleString()}` : null}
            </Typography>
          </Box>
          <Chip label="Bypass" size="small" color="warning" variant="outlined" />
        </Stack>
      </Box>

      {/* Scrollable parameter rows */}
      <TableContainer sx={{ flexGrow: 1, overflowY: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Parameter</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Value</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Unit</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Reference range</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(!results || results.length === 0) && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography variant="caption" color="text.secondary">
                    No parameter rows.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {(results || []).map((r: any, i: number) => {
              const status = r.vendorMapping?.mappingStatus;
              const statusColor =
                status === 'auto_matched' || status === 'nurse_confirmed' ? 'success.main'
                : status === 'suggested' ? 'info.main'
                : status === 'unmapped' || !status ? 'warning.main'
                : 'text.secondary';
              return (
                <TableRow key={i} sx={{ '& td': { fontSize: 12, py: 0.6 } }}>
                  <TableCell>
                    <Tooltip
                      title={
                        r.vendorMapping?.standardizedName
                          ? `Mapped → ${r.vendorMapping.standardizedName}`
                          : 'Unmapped'
                      }
                      placement="right"
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: statusColor, flexShrink: 0 }} />
                        <span>{r.serviceItemName}</span>
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{r.value}</TableCell>
                  <TableCell>{r.unit || '—'}</TableCell>
                  <TableCell>{r.referenceRange?.referenceRange || '—'}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default BypassCsvPanel;
